import {
	AsyncSelect,
	Inline,
	Box,
	Button,
	Table,
	Text,
	Stack,
	Heading,
	Col,
	Divider,
	Grid,
	Icons,
	Container,
	joinAttributes,
	Badge,
	Select,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { Fragment, useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Omit, Optional } from "utility-types"
import { SelectAccount } from "../Accounting"
import * as Validator from "yup"
import { generatePath } from "../router-utils"
import { IAccount } from "../Accounting"
import {
	Form,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	SelectField,
	TextInputField,
	SwitchInputField,
	GetFieldValue,
	FormApi,
	TextAreaInputField,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

import { type T_BankAccount, bankAccountsXHR } from "./store"
import { BankBranchDetailsForCode, TWorldBankBranch } from "../World"
import {
	SelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import { TTripDestination } from "../TripDestinations"
import { TTenantBrand } from "../Brands"

export function BankAccountsList() {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)
	const [isAddAccountVisible, showAddNewAccount, hideAddNewAccount] =
		useDialog()
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Fragment>
			<Helmet>
				<title>Bank Accounts</title>
			</Helmet>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Bank Accounts"
			>
				<ListView<T_BankAccount>
					pageKey="bank-accounts"
					fetch={(xhr, params) => bankAccountsXHR(xhr).getBankAccounts(params)}
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
					emptyState={({ searched, defaultValue }) =>
						searched ? (
							defaultValue
						) : (
							<Container paddingY="8" textAlign="center">
								<Stack gap="4">
									<Heading>Bank Account</Heading>
									<Text>
										Save bank account details and easily share them with your
										clients.
									</Text>
									<Box>
										<Button level="primary" onClick={showAddNewAccount}>
											Add Bank Account Details
										</Button>
									</Box>
								</Stack>
							</Container>
						)
					}
				>
					{({ items: bankAccounts, refresh }) => (
						<BankAccountsTableView
							bankAccounts={bankAccounts}
							onChange={refresh}
						/>
					)}
				</ListView>
			</Search>
			<AddBankAccountInDialog
				open={isAddAccountVisible}
				onCancel={hideAddNewAccount}
				onSuccess={() => {
					setParams({ ...params, page: 1 })
					hideAddNewAccount()
				}}
			/>
		</Fragment>
	)
}

export function BankAccountsTableView({
	destinationView,
	onChange,
	bankAccounts,
	tripDestinations,
}: {
	destinationView?: boolean
	onChange: () => unknown
	bankAccounts: Array<T_BankAccount>
	tripDestinations?: TTenantBrand["trip_destinations"]
}) {
	return (
		<Table
			headers={[
				"Name",
				"Bank Branch",
				"Account Number",
				"Linked Account",
			].concat(!destinationView ? ["Trip Destinations", ""] : [])}
			bordered
			striped
			responsive
			rows={bankAccounts.map((bankAccount) => {
				const {
					account_number,
					account_iban,
					beneficiary_name,
					bank_name,
					branch_name,
					branch_ifsc_code,
					branch_swift_code,
					accounting_account,
					currency,
					additional_details,
					trip_destinations,
				} = bankAccount
				return [
					<Stack gap="1">
						<Text>{beneficiary_name}</Text>
						{currency ? (
							<Box>
								<Badge outlined primary>
									{currency}
								</Badge>
							</Box>
						) : null}
					</Stack>,
					<Stack>
						<Text fontWeight="semibold">{bank_name}</Text>
						<Text fontSize="sm">
							{joinAttributes(branch_name, branch_ifsc_code)}
						</Text>
						{branch_swift_code ? (
							<Text fontSize="sm">SWIFT Code: {branch_swift_code}</Text>
						) : null}
					</Stack>,
					<Stack>
						<Text>{account_number}</Text>
						{account_iban ? (
							<Text fontSize="sm">IBAN: {account_iban}</Text>
						) : null}
						{additional_details
							? additional_details.split("\n").map((d, index) => (
									<Text fontSize="sm" key={index}>
										{d}
									</Text>
								))
							: null}
					</Stack>,
					accounting_account ? (
						<Stack>
							<Link
								to={generatePath("/accounting/accounts/:accountId", {
									accountId: accounting_account.id.toString(),
								})}
								anchored
								color="accent"
							>
								{accounting_account.name}
							</Link>
							{accounting_account.description ? (
								<Text fontSize="sm" color="muted" maxWidth="xs">
									{accounting_account.description}
								</Text>
							) : null}
						</Stack>
					) : (
						"N/A"
					),
					...(!destinationView
						? [
								trip_destinations?.length ? (
									<Stack gap="px">
										{trip_destinations.map((t) => (
											<Text key={t.id} fontSize="sm">
												{t.name}
											</Text>
										))}
									</Stack>
								) : (
									"-"
								),
								<BankAccountActions
									tripDestinations={tripDestinations}
									bankAccount={bankAccount}
									onChange={onChange}
								/>,
							]
						: []),
				]
			})}
		/>
	)
}

function BankAccountActions({
	tripDestinations,
	bankAccount,
	onChange,
}: {
	tripDestinations?: TTenantBrand["trip_destinations"]
	bankAccount: T_BankAccount
	onChange: () => unknown
}) {
	const xhr = useXHR()
	const [isEditOpen, openEdit, closeEdit] = useDialog()
	return (
		<Inline gap="2">
			<Button
				size="sm"
				level="tertiary"
				onClick={openEdit}
				title="Edit Bank Account Destinations"
			>
				<Icons.Pencil />
			</Button>
			<Dialog
				open={isEditOpen}
				onClose={closeEdit}
				title="Update Bank Account Destinations"
				sm
			>
				<Dialog.Body>
					<Form<Pick<T_BankAccountFormData, "trip_destinations">>
						onSubmit={async (values) => {
							const payload = {
								trip_destinations: values.trip_destinations.map((d) => d.id),
							}
							await bankAccountsXHR(xhr).updateBankAccountDestinations(
								bankAccount.id,
								payload
							)
							onChange()
							closeEdit()
							showSnackbar("Bank account destinations updated successfully.")
						}}
						initialValues={{
							trip_destinations: bankAccount.trip_destinations || [],
						}}
					>
						{({ handleSubmit, submitting }) => (
							<form noValidate onSubmit={handleSubmit}>
								<SelectField
									select={Select}
									options={tripDestinations || []}
									name="trip_destinations"
									label="Trip Destinations"
									multiple
								/>
								<Divider sm />
								<Inline gap="4" marginTop="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Updating..." : "Update"}
									</Button>
									<Button
										onClick={closeEdit}
										level="tertiary"
										disabled={submitting}
									>
										Cancel
									</Button>
								</Inline>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
			<ConfirmationDialogProvider>
				{({ confirm }) => (
					<>
						<Button
							onClick={() => {
								confirm().then(async (yes) => {
									if (!yes) return
									try {
										const { message } = await bankAccountsXHR(
											xhr
										).deleteBankAccount(bankAccount.id)
										showSnackbar(message)
										onChange()
									} catch (e) {
										showSnackbar(
											(e as Error).message ||
												"Something went wrong. Please try later."
										)
									}
								})
							}}
							size="sm"
							level="tertiary"
							status="danger"
						>
							<Icons.Trash />
						</Button>
						<ConfirmationDialog title="Delete Bank Account" destructiveAction>
							<Stack gap="4">
								<Stack gap="2">
									<Text color="danger" fontWeight="semibold">
										<Icons.Attention /> Once deleted, bank account can not be
										restored.
									</Text>
									<Text fontSize="sm" color="muted">
										You will be able to add new one.
									</Text>
								</Stack>
								<Text>Are you sure you want to delete this bank account ?</Text>
							</Stack>
						</ConfirmationDialog>
					</>
				)}
			</ConfirmationDialogProvider>
		</Inline>
	)
}

type SelectBankAccountsProps = Omit<
	React.ComponentProps<typeof AsyncSelect>,
	"fetch"
> & {
	currency?: string
	tenant_brands?: Array<number>
}

export function SelectBankAccounts({
	currency,
	tenant_brands,
	...otherProps
}: SelectBankAccountsProps) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<
		((bankAccount: T_BankAccount) => void) | undefined
	>(undefined)
	return (
		<Fragment>
			<AsyncSelect
				multiple
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Stack gap="1">
							{option.name}

							<Text fontSize="xs" color="muted">
								{joinAttributes(
									option.branch_ifsc_code || option.branch_swift_code,
									option.branch_city_name
								)}
							</Text>
						</Stack>
					)
				}
				{...otherProps}
				fetch={(q) =>
					bankAccountsXHR(xhr)
						.getBankAccounts({ q, currency, tenant_brands })
						.then((resp) => resp.data.map((b) => ({ ...b })))
				}
			/>
			<AddBankAccountInDialog
				open={!!newName}
				initialValues={{
					tenant_brand: tenant_brands ? tenant_brands[0] : undefined,
				}}
				onCancel={() => {
					setNewName("")
				}}
				onSuccess={(bankAccount) => {
					promiseOfCreation.current?.(bankAccount)
					setNewName("")
				}}
			/>
		</Fragment>
	)
}

function AddBankAccountInDialog({
	open,
	onCancel,
	onSuccess,
	...props
}: Optional<React.ComponentProps<typeof AddBankAccount>, "onSuccess"> & {
	open: boolean
}) {
	return (
		<Dialog open={open} onClose={onCancel} title="Add Bank Account" xl>
			<Dialog.Body>
				<AddBankAccount
					onSuccess={(bankAccount) => {
						onSuccess ? onSuccess(bankAccount) : onCancel()
					}}
					onCancel={() => {
						onCancel()
					}}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function AddBankAccount({
	onSuccess,
	...props
}: Omit<React.ComponentProps<typeof AddBankAccountForm>, "onSubmit"> & {
	onSuccess: (bankAccount: T_BankAccount) => void
}) {
	const xhr = useXHR()
	return (
		<AddBankAccountForm
			onSubmit={async ({
				accounting_account,
				accounting_account_description,
				currency,
				...values
			}) => {
				const data = await bankAccountsXHR(xhr).storeBankAccount({
					...values,
					accounting_account: accounting_account?.id || null,
					accounting_account_description: accounting_account
						? null
						: accounting_account_description,
					currency: currency,
					trip_destinations: values.trip_destinations.map((d) => d.id),
				})
				onSuccess(data)
				return data
			}}
			{...props}
		/>
	)
}

const addBankAccountValidationSchema = Validator.object().shape({
	account_number: Validator.string()
		.required("Please provide your bank account number")
		.max(100, "Please use 100 or less characters for account number"),
	currency: Validator.mixed().required("Please select the bank currency"),
	beneficiary_name: Validator.string().required(
		"Please provide account/beneficiary name for the account"
	),
	bank_name: Validator.string().required("Please enter the bank name"),
	bank_code: Validator.string().required(
		"Please enter the bank code or short name"
	),
	branch_name: Validator.string().required(
		"Please enter the name of the branch"
	),
	branch_country_name: Validator.string().required("Required"),
	branch_state_name: Validator.string().required("Required"),
	branch_city_name: Validator.string().required("Required"),
	branch_address: Validator.string().required(
		"Please enter the address of the branch"
	),
	new_accounting_account: Validator.number().required(
		"Please select or deselect the checkbox"
	),
	accounting_account: Validator.object().when(
		"new_accounting_account",
		([value], schema) => {
			const addNewAccount = Number(value)
			if (!addNewAccount)
				return schema.required(
					"Please select an account or select the checkbox to create new."
				)
			return schema
		}
	),
	trip_destinations: Validator.array().of(Validator.mixed()),
})

type T_BankAccountFormData = {
	account_number: string
	currency: string
	branch_ifsc_code: string
	branch_swift_code: string
	branch_micr_code: string
	beneficiary_name: string
	accounting_account?: IAccount
	accounting_account_description?: string
	new_accounting_account: 0 | 1
	branch_search_using: "ifsc" | "swift_code"
	branch_country_name: string
	bank_name?: string
	bank_code?: string
	branch_name?: string
	branch_state_name?: string
	branch_city_name?: string
	branch_address?: string
	tenant_brand?: number
	additional_details?: string
	trip_destinations: Array<TTripDestination>
}

export function AddBankAccountForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	tenantBrand,
}: {
	onSubmit: (
		values: Omit<T_BankAccountFormData, "branch_search_using">
	) => Promise<T_BankAccount>
	onCancel: () => void
	initialValues?: Partial<T_BankAccountFormData>
	tenantBrand?: TTenantBrand
}) {
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const [initialValues] = useState<T_BankAccountFormData>(() => ({
		account_number: "",
		currency: functionalCurrency.currency || "INR",
		branch_ifsc_code: "",
		branch_swift_code: "",
		branch_micr_code: "",
		beneficiary_name: "",
		accounting_account: undefined,
		accounting_account_description: undefined,
		new_accounting_account: 0,
		country: undefined,
		branch_search_using:
			functionalCurrency.currency === "INR" ? "ifsc" : "swift_code",
		bank_name: "",
		bank_code: "",
		branch_name: "",
		branch_city_name: "",
		branch_state_name: "",
		branch_country_name: "",
		branch_address: "",
		tenant_brand: undefined,
		trip_destinations: [],
		...propInitialValues,
	}))

	function autoFillBranchDetailsFromSearchResults(
		form: FormApi<T_BankAccountFormData>,
		branch: TWorldBankBranch
	) {
		form.change("bank_name", branch.bank.name)
		form.change("bank_code", branch.bank.code)
		form.change("branch_name", branch.name)
		form.change("branch_city_name", branch.city_name || "")
		form.change("branch_state_name", branch.state_name || "")
		form.change("branch_country_name", branch.country_name || "")
		form.change(
			"branch_ifsc_code",
			String(branch.ifsc_code || "").toUpperCase()
		)
		form.change(
			"branch_swift_code",
			String(branch.swift_code || "").toUpperCase()
		)
		form.change("branch_micr_code", branch.micr_code || "")
		form.change("branch_address", branch.address || "")
	}
	return (
		<Form<T_BankAccountFormData>
			initialValues={initialValues}
			validate={validateFormValues(addBankAccountValidationSchema)}
			onSubmit={withServerErrors(({ branch_search_using, ...values }) => {
				return onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col xs={12} sm={4}>
							<Stack gap="1">
								<Heading as="h4">Bank Branch Details</Heading>
								<Text color="muted">
									Please provide the respective bank details.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<GetFieldValue<
									"ifsc" | "swift_code"
								> name="branch_search_using">
									{({ value: branchSearchUsing }) =>
										branchSearchUsing === "ifsc" ? (
											<Stack gap="4" key="ifsc">
												<TextInputField
													label="Search by IFSC Code"
													name="branch_ifsc_code"
													type="text"
													placeholder="e.g. HDFC0001719"
													minLength={11}
													maxLength={11}
												/>
												<GetFieldValue<string> name="branch_ifsc_code">
													{({ value: code }) =>
														String(code).length === 11 ? (
															<BankBranchDetailsForCode
																type="ifsc"
																code={code}
																onSelect={(branch) => {
																	autoFillBranchDetailsFromSearchResults(
																		form,
																		branch
																	)
																}}
															/>
														) : null
													}
												</GetFieldValue>
											</Stack>
										) : (
											<Stack gap="4" key="branch_swift_code">
												<TextInputField
													label="Search by SWIFT Code"
													name="swift_code"
													type="text"
													placeholder="e.g. HDFCINBBXXX"
													minLength={8}
													maxLength={11}
												/>
												<GetFieldValue<string> name="branch_swift_code">
													{({ value: code }) =>
														String(code).length >= 8 &&
														String(code).length <= 11 ? (
															<BankBranchDetailsForCode
																type="swift_code"
																code={code}
																onSelect={(branch) => {
																	autoFillBranchDetailsFromSearchResults(
																		form,
																		branch
																	)
																}}
															/>
														) : null
													}
												</GetFieldValue>
											</Stack>
										)
									}
								</GetFieldValue>
							</Stack>
							<Divider sm />
							<Stack gap="4">
								<Heading fontSize="md" color="primary">
									Bank Details
								</Heading>
								<Inline collapseBelow="sm" gap="4">
									<Box flex="1">
										<TextInputField
											name="bank_name"
											label="Name of the Bank"
											placeholder="e.g. HDFC Bank"
										/>
									</Box>
									<TextInputField
										name="bank_code"
										type="text"
										label="Bank Short Name / Code"
										placeholder="HDFC"
									/>
								</Inline>
								<Divider marginY="0" />
								<Heading fontSize="md" color="primary">
									Branch Details
								</Heading>
								<TextInputField
									name="branch_name"
									label="Branch Name"
									type="text"
									placeholder="e.g. City Center"
									required
								/>
								<Inline gap="4">
									{functionalCurrency.currency === "INR" ? (
										<TextInputField
											name="branch_ifsc_code"
											type="text"
											label="IFSC"
											placeholder="e.g. HDFC0001719"
										/>
									) : null}
									<TextInputField
										name="branch_swift_code"
										type="text"
										label="SWIFT Code"
										placeholder="e.g. HDFCINBBXXX"
									/>
									<TextInputField
										name="branch_micr_code"
										type="number"
										label="MICR Code"
										secondaryLabel="optional"
										placeholder="e.g. 12121212"
									/>
								</Inline>
								<Inline gap="4" collapseBelow="sm">
									<TextInputField
										name="branch_country_name"
										label="Country"
										type="text"
										placeholder="e.g. India"
									/>
									<TextInputField
										name="branch_state_name"
										type="text"
										label="Branch State / Province"
										placeholder="e.g. Raj"
										required
									/>
									<TextInputField
										name="branch_city_name"
										label="Branch City / District Name"
										type="text"
										placeholder="e.g. Jaip"
										required
									/>
								</Inline>
								<TextInputField
									name="branch_address"
									label="Branch Address"
									type="text"
									placeholder="e.g. City Center, 123123"
									required
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider />
					<Grid gap="4">
						<Col xs={12} sm={4}>
							<Stack gap="1">
								<Heading as="h4">Account Details</Heading>
								<Text color="muted">
									Please provide your bank's account number, name.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<Grid gap="4">
									<Col sm={12} md>
										<TextInputField
											label="Account Number"
											name="account_number"
											type="text"
											placeholder="e.g. 5010016324890"
											required
											width="full"
										/>
									</Col>
									<Col>
										<TextInputField
											label="Account/Beneficiary Name"
											name="beneficiary_name"
											type="text"
											placeholder="e.g. Your Business Account"
											required
										/>
									</Col>
								</Grid>
								<Box>
									<Grid gap="4">
										<Col sm={12} md={3}>
											<SelectTenantCurrencyInputField
												name="currency"
												label="Account Currency"
												required
												placeholder="Select a Currency"
											/>
										</Col>
										<Col>
											<TextInputField
												label="Account IBAN"
												name="account_iban"
												type="text"
												secondaryLabel="optional"
												placeholder="e.g. IN29NWBK60161331926819"
											/>
										</Col>
									</Grid>
								</Box>
								<TextAreaInputField
									label="Additional Details"
									name="additional_details"
									placeholder={"ABC: 123123\nCODE: 123123"}
									secondaryLabel="optional"
									help="Use line breaks and colons (:) for formatting. When sharing accounts, these details will be shared."
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider />
					<Grid gap="4">
						<Col xs={12} sm={4}>
							<Stack gap="1">
								<Heading as="h4">Linked Sembark Account</Heading>
								<Text color="muted">
									Please select/create the virtual Sembark account to keep track
									of all your transactions at one place.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<Grid gap="4">
									<Col sm={12} md>
										<SwitchInputField
											name="new_accounting_account"
											label="Create new account on Sembark"
											help="An account will be automatically created for this bank account and the selected currency."
										/>
									</Col>
									<Col sm={12} md="auto">
										<Box textAlign="center" marginY="4" fontWeight="semibold">
											OR
										</Box>
									</Col>
									<Col>
										<GetFieldValue<
											boolean | undefined
										> name="new_accounting_account">
											{({ value: new_accounting_account }) => (
												<GetFieldValue<string> name="currency">
													{({ value: currency }) => (
														<SelectField
															name="accounting_account"
															label="Select an Account"
															select={SelectAccount}
															disableLinkedBankAccounts={true}
															params={{ entity_type: "tenants" }}
															disabled={Boolean(new_accounting_account)}
															help={({ value }) => {
																if (value && !new_accounting_account) {
																	const account = value as IAccount
																	const asset_type = account?.asset_type

																	if (asset_type && asset_type !== currency) {
																		return (
																			<Text color="warning" fontSize="sm">
																				Note: The selected account's currency (
																				{asset_type}) differs from your bank
																				account's currency ({currency}). The
																				account's currency will be updated to{" "}
																				{currency}
																			</Text>
																		)
																	} else if (!asset_type) {
																		return (
																			<Text color="warning" fontSize="sm">
																				This account's currency will be updated
																				to {currency}
																			</Text>
																		)
																	}
																}
															}}
														/>
													)}
												</GetFieldValue>
											)}
										</GetFieldValue>
									</Col>
								</Grid>
								<GetFieldValue<
									boolean | undefined
								> name="new_accounting_account">
									{({ value: new_accounting_account }) =>
										new_accounting_account ? (
											<TextInputField
												label="Account Description/Notes"
												secondaryLabel="optional"
												name="accounting_account_description"
												type="text"
												placeholder="e.g. This account is primary used for xyz."
											/>
										) : null
									}
								</GetFieldValue>
							</Stack>
						</Col>
					</Grid>
					<Divider sm />
					<Grid gap="4">
						<Col xs={12} sm={4}>
							<Stack gap="1">
								<Heading as="h4">
									<Icons.Map opacity="70" size="6" /> Trip Destinations
								</Heading>
								<Text color="muted">
									Please select destinations to attach the address to specific
									destinations.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Stack gap="4">
								<SelectField
									select={Select}
									options={tenantBrand?.trip_destinations}
									name="trip_destinations"
									label="Trip Destinations"
									multiple
								/>
							</Stack>
						</Col>
					</Grid>
					<Divider />
					<Grid gap="0">
						<Col xs={12} sm={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ sm: "4" }}>
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save Account Details"}
									</Button>
									<Button
										disabled={submitting}
										onClick={onCancel}
										level="tertiary"
									>
										Cancel
									</Button>
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}
